// File: js/db/queue-clock.js
//
// The monotonic queue clock (spec §7).
//
// Every outbox row's `queued_at` comes from here. `Date.now()` collides freely
// within a millisecond, and when two outbox rows share a `queued_at` Dexie breaks
// the tie on the next key — the RANDOM UUID — so two causally-ordered writes taken
// in the same millisecond can drain in the reverse order they happened. A plain
// fixed tick (`t, t+1, t+2…`) is no fix either: it borrows future milliseconds a
// later concurrent write could legitimately land inside.
//
// `next = max(now, last + 1)` is strictly greater than every value previously
// issued, whatever the wall clock does — including a clock that jumps backwards.

/** Module-level high-water mark: the largest `queued_at` ever issued here. */
let last = 0;

/**
 * Issue the next `queued_at`.
 *
 * @param {{now: () => number}} [clock] Injectable time source; defaults to `Date.now()`.
 * @returns {number} Strictly greater than every previous return value.
 */
export function nextQueuedAt(clock) {
    const now = clock && typeof clock.now === 'function' ? clock.now() : Date.now();
    last = Math.max(now, last + 1);
    return last;
}

/**
 * The high-water mark, without issuing anything. Diagnostics only.
 * @returns {number}
 */
export function lastQueuedAt() {
    return last;
}

/**
 * TEST-ONLY: reset the high-water mark so tests can assert exact values without
 * cross-test bleed. Never called by production code.
 * @param {number} [value=0]
 */
export function _resetQueueClockForTests(value = 0) {
    last = value;
}
